import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import Tibbit_00 from '@appblocks/codegeneration/Tibbits/Tibbit_00';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
    IOLinesMode,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import IOTibbit from '@appblocks/codegeneration/Tibbits/_IOTibbit';

export default class KeypadNode extends BaseNode {
    key_event: string;

    constructor() {
        super();
        this.name = 'On Button Pressed';
        this.id = 'node_keypad';
        this.linkedFeature = 'general';
        this.icon = 'RxButton';
        this.requirements = ['tios'];
        this.key_event = 'PL_KP_EVENT_PRESSED';

        this.outputs = [
            {
                name: 'Button Pressed',
                portId: 'on_kp',
                params: [],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'BUTTON',
                name: 'Button',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                options: [],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDependencies() {
        const dependencies = [
            'kp',
        ];
        return dependencies;
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const BUTTON = this.getSettingValueOptionName('BUTTON');
            if (BUTTON) {
                return `On {{$${BUTTON}}} ${this.name.split(' ').pop()}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const BUTTON = this.getSettingValueOptionName('BUTTON');
        if (BUTTON) {
            return `On {{$${BUTTON}}} ${this.name.split(' ').pop()}`;
        }
        return this.name;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId !== 'BUTTON') return super.getSettingOptions(settingId);
        const project: ProjectConfiguration = this.project;
        const options = [];
        options.push({
            value: '',
            name: 'Undefined',
        });
        for (let i = 0; i < project.features.length; i += 1) {
            const feature: any = project.features[i];
            const tibbit = project.features[i].getSettingValue('TIBBIT');
            let variable;
            let lines;
            if (tibbit === '38') {
                variable = project.features[i].getSettingValue('BUTTON_NAME');
                options.push({
                    name: variable,
                    value: variable,
                });
            }
            // see if feature is subclass of IOTibbit
            if (tibbit !== undefined && feature.lines !== undefined && feature.mode !== undefined) {
                lines = feature.lines;
                for (let j = 1; j <= lines; j += 1) {
                    if (project.features[i].getSettingValue(`L${j}MODE`) === IOLinesMode.Button) {
                        variable = `${project.features[i].getSettingValue(`L${j}NAME`)}_kp`;
                        options.push({
                            name: variable,
                            value: variable,
                        });
                    }
                }
            }
            if (project.features[i].id === 'lcd') {
                if (project.features[i].getSettingValue('ENABLED') === '1') {
                    for (let j = 0; j < 4; j++) {
                        options.push({
                            name: `KEY_${j + 1}`,
                            value: `LCD_KEYPAD_${j + 1}`,
                        });
                    }
                }
            }
        }
        return options;
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const BUTTON = this.getSettingValue('BUTTON');
        if (BUTTON === '') {
            errors.push({
                message: 'Button is not defined.',
                messageType: ValidationMessageType.ERROR,
                settingId: 'BUTTON',
                featureId: this.getID(),
            });
        }
        return errors;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const button = this.getSettingValue('BUTTON');
        const links = project.getNodeLinks(this, this.outputs[0].portId);
        let code = '';
        for (let i = 0; i < links.length; i++) {
            const link = links[i];
            code += `${project.getCodeLocationStart()}${link.getSourceId()}${project.getCodeLocationEnd()}\r\n`;
        }

        const features = this.project.features;
        let enterLCDNodeFound = false;
        for (let i = 0; i < features.length; i++) {
            if (features[i].id === 'node_lcd_menu') {
                enterLCDNodeFound = true;
                break;
            }
        }

        let lcdButtonCode = '';
        if (button.startsWith('LCD_KEYPAD') && enterLCDNodeFound) {
            lcdButtonCode = ' and ui_active=false';
        }
        if (project.runtime === 'simulator') {
            blocks.push({
                location: 'button_triggered',
                blockType: BlockType.CODE,
                template: `
    if (state === ${this.key_event} && button === ${button.startsWith('LCD') ? button : `"${button}"`}) {
        ${code}
    }`,
            });
        } else {
            blocks.push({
                location: 'on_kp',
                blockType: BlockType.CODE,
                template: `
    if key_event=${this.key_event} and key_code = ${button}${lcdButtonCode} then
        ${code}
    end if`,
            });
        }

        return blocks;
    }

    getDetails(): string {
        return `This block is triggered when a keypad button is ${this.name.split(' ').pop()?.toLowerCase()}.`;
    }
}
